import {Component, OnDestroy, ViewChild} from '@angular/core';
import {Subject, takeUntil} from "rxjs";
import {Store} from "@ngrx/store";
import * as fromRoot from "../../../../../state/app.state";
import {usersList} from "../../core/manage-users.selectors";
import {
  FetchUsersListRequest,
  ResendInviteRequest
} from '../../core/manage-users.actions';
import {
  groupStatusColors,
  indicatorColors,
  roles,
  roleUrlMap,
  statusLabelMap, userStatusColors,
  userStatusMap
} from '../../../../shared/constants/common.constants';
import {ActivatedRoute, Router} from "@angular/router";
import {
  drillDownUrls,
  profile,
  selectedUserProfile
} from '../../../../shared/core/shared.selectors';
import {UsersList} from 'src/app/models/users-list';
import {ManageUsersService} from '../../core/manage-users.service';
import {CommonService} from '../../../../shared/services/common.service';
import {MatSort, Sort} from "@angular/material/sort";
import {
  DeleteUserRequest,
  SetDrillDownUrls
} from "../../../../shared/core/shared.actions";
import {MatTable, MatTableDataSource} from "@angular/material/table";
import {
  ChangeUserStatusComponent
} from "../change-user-status/change-user-status.component";
import {
  ChangeParticipantsGroupComponent
} from "../change-participants-group/change-participants-group.component";
import {FetchPreviewInviteDataRequest} from '../../../invite/core/invite.actions';


@Component({
  selector: 'app-manage-users-table',
  templateUrl: './manage-users-table.component.html',
  styleUrls: ['./manage-users-table.component.css']
})
export class ManageUsersTableComponent implements OnDestroy {
  @ViewChild(MatTable) table: MatTable<any>;
  @ViewChild(MatSort) sort: MatSort;

  loading: boolean = false;
  userListData: UsersList;
  onApplyUserListFilter: any;
  tableData: any[];
  dataSource: any = new MatTableDataSource();
  displayedColumns = [
    'name',
    'role',
    'status',
    'client',
    'group',
    'groupStatus',
    'email',
    'gender',
    'preferredPronouns',
    'createdAt',
    'actionMenu',
  ];
  role: number;
  roles = roles;
  userRole: number;

  sortColumn: any = 'sort_userId'
  sortOrder: any = 'desc'

  isFirstPage = true;

  selectedUserId: number | null;
  selectedUserRole: number | null;

  protected readonly statusLabelMap = statusLabelMap;
  protected readonly roleUrlMap = roleUrlMap;
  protected readonly userStatusMap = userStatusMap;
  private readonly OnDestroy: Subject<any> = new Subject<any>();

  constructor(
    private router: Router,
    private manageUsersService: ManageUsersService,
    private store: Store<fromRoot.State>,
    private commonService: CommonService,
    private route: ActivatedRoute
  ) {
    this.manageUsersService.onUserFilterChange.next(null);
    this.manageUsersService.fetchUsersListTrigger
      .pipe(takeUntil(this.OnDestroy))
      .subscribe(() => {
        this.fetchUserList();
      });

    this.store.select(profile)
      .pipe(takeUntil(this.OnDestroy))
      .subscribe((data) => {
        this.userRole = data?.role
        if (this.userRole !== roles.superAdmin) {
          this.displayedColumns.splice(this.displayedColumns.indexOf('client'), 1);
        }
      });

    this.store.select(selectedUserProfile)
      .pipe(takeUntil(this.OnDestroy))
      .subscribe((data) => {
        this.selectedUserRole = data?.profile?.role;
      });

    this.fetchUserList();

    this.store.select(usersList)
      .pipe(takeUntil(this.OnDestroy))
      .subscribe(data => {
        this.loading = data.loading;
        this.userListData = data;


        if (!data.loading) {
          this.dataSource.data = [...data.list];

          if (data.hasNext) {
            setTimeout(() => {
              if (this.sortColumn && data.pageNumber === 1) {
                this.commonService.scrollColumnInView(this.sortColumn);
              }
            }, 0)

            setTimeout(() => {
              // check window height for infinite scroll
              this.commonService.checkInfiniteScrollHeightAndCallNext('table-wrapper', () => {
                this.onScroll();
              });
            }, 0)
          }
        }


      });

    // listen for filter changes from the filter component
    this.manageUsersService.onUserFilterChange.pipe(takeUntil(this.OnDestroy))
      .subscribe((data) => {
        this.onApplyUserListFilter = data;
        this.isFirstPage = true;
        this.fetchUserList();
      })
  }

  fetchUserList(pageNumber = 1, limit = 10) {
    const params: any = this.route.snapshot.params;

    this.selectedUserId = params?.clientAdminId ||
      params?.coachId ||
      params?.memberId;

    this.store.dispatch(FetchUsersListRequest({
      payload: {
        ...this.onApplyUserListFilter,
        pageNumber,
        limit,
        ...(this.sortOrder ? {sortOrder: this.sortOrder} : {}),
        ...(this.sortOrder ? {sortColumn: this.sortColumn} : {}),
        ...(this.selectedUserId ? {forUserId: this.selectedUserId} : {})
      }
    }));
  }

  onScroll() {
    if (this.userListData?.hasNext && !this.userListData?.loading) {
      this.fetchUserList(this.userListData.pageNumber + 1, this.userListData.limit);
      this.isFirstPage = false;

    }
  }

  selectUser(user) {
    if (user?.userId) {
      this.store.select(drillDownUrls).subscribe(data => {
        const updatedDrillDownUrls = [...data, this.router.url];
        if ([...data].pop() !== this.router.url) {
          this.store.dispatch(SetDrillDownUrls({payload: updatedDrillDownUrls}));
        }
      }).unsubscribe();

      const urlPrefix = this.router.url.split('/')[1];

      const role = user.role.key;

      if (role === roles.adult || role === roles.participant) {
        this.router.navigate([`/${urlPrefix}/member/${user.userId}/behavior-tracker`]);
      } else if (role === roles.coach) {
        this.router.navigate([`/${urlPrefix}/${this.roleUrlMap[role]}/${user.userId}/groups`]);
      } else if (role === roles.clientAdmin) {
        this.router.navigate([`/${urlPrefix}/${this.roleUrlMap[role]}/${user.userId}/dashboard`]);
      } else {
        this.router.navigate([`/${urlPrefix}/${this.roleUrlMap[role]}/${user.userId}`]);
      }
    }
  }

  openClinicDashboard(user: any) {
    const urlPrefix = this.router.url.split('/')[1];
    this.router.navigate([`/${urlPrefix}/organization/${user.clientId}/dashboard`]);
  }

  openGroupDetails(user: any) {
    if (!user?.groupInfo?.isGroupAccessible) {
      // skip if group is not accessible
      return;
    }
    const urlPrefix = this.router.url.split('/')[1];
    this.router.navigate([`/${urlPrefix}/group/${user?.groupInfo?.id}/dashboard`]);
  }


  sortData(sort: Sort) {
    this.isFirstPage = true;
    this.sortColumn = `sort_${sort.active}`;
    this.sortOrder = sort.direction;

    this.fetchUserList();
  }

  changeUserStatus(data) {
    this.commonService.openDialog({
      data: {
        component: ChangeUserStatusComponent,
        userData: data,
      },
      panelWidth: 30,
    }).afterClosed().subscribe(() => {
      this.fetchUserList()
    });


  }

  ChangeParticipantsGroup(data) {
    this.commonService.openDialog({
      data: {
        component: ChangeParticipantsGroupComponent,
        participantData: data,
      },
      panelWidth: 30,
    });


  }

  resendInvite(userDetails) {
    this.store.dispatch(ResendInviteRequest({payload: {userId: userDetails.userId}}));
  }

  previewInvite(userDetails) {
    this.store.dispatch(FetchPreviewInviteDataRequest({userId: userDetails.userId}));
  }

  deleteUser(userDetails) {
    this.commonService.openConfirmationDialog({
        data: {
          message: 'Are you sure you want to continue with delete?',
          submitText: 'Yes',
          classList: 'w-[40vw]',
          handleSubmit: () => {
            this.store.dispatch(DeleteUserRequest({payload: {userId: userDetails.userId}}));
          },
          cancelText: 'No',
          handleCancel: () => {
            this.commonService.closeDialog();
          }
        }
      }
    );
  }

  ngOnDestroy() {
    this.OnDestroy.next(null);
    this.OnDestroy.complete();
  }

  protected readonly userStatusColors = userStatusColors;
  protected readonly groupStatusColors = groupStatusColors;
}
